export interface VoiceSettings {
	stability: number
	similarity_boost: number
	style: number
	use_speaker_boost?: boolean
	speed?: number
}

export interface TextToSpeechOptions {
	text: string
	voiceId: string
	modelId?: string
	voiceSettings?: VoiceSettings
	outputFormat?: string
}

export interface GenerateSpeechParams {
	text: string
	voiceId: string
	speed?: number
	expressiveness?: number
	clarity?: number
	styleExaggeration?: number
}

export interface SpeechToSpeechParams {
	voiceId: string
	stability?: number
	similarityBoost?: number
	style?: number
	useSpeakerBoost?: boolean
	modelId?: string
	outputFormat?: string
	removeBackgroundNoise?: boolean
}

class ElevenLabsService {
	private apiKey: string
	private baseUrl = 'https://api.elevenlabs.io/v1'

	constructor() {
		const apiKey = import.meta.env?.VITE_ELEVEN_LABS_API_KEY ?? ''
		if (!apiKey) {
			throw new Error('VITE_ELEVEN_LABS_API_KEY is not configured')
		}
		this.apiKey = apiKey
	}

	private normalizeSliderValue(value: number): number {
		return Math.max(0, Math.min(100, value)) / 100
	}

	private mapToVoiceSettings(params: GenerateSpeechParams): VoiceSettings {
		const expressiveness = this.normalizeSliderValue(
			params.expressiveness ?? 50
		)
		const clarity = this.normalizeSliderValue(params.clarity ?? 75)
		const styleExaggeration = this.normalizeSliderValue(
			params.styleExaggeration ?? 75
		)

		const speedNormalized = this.normalizeSliderValue(params.speed ?? 50)
		const speed = 0.7 + speedNormalized * 0.5

		return {
			stability: 1.0 - expressiveness,
			similarity_boost: clarity,
			style: styleExaggeration,
			use_speaker_boost: clarity > 0.5,
			speed: speed,
		}
	}

	async generateSpeech(params: GenerateSpeechParams): Promise<Blob> {
		const { text, voiceId } = params

		if (!text || !voiceId) {
			throw new Error('Text and voiceId are required')
		}

		const voiceSettings = this.mapToVoiceSettings(params)

		const requestBody = {
			text,
			model_id: 'eleven_multilingual_v2',
			voice_settings: voiceSettings,
		}

		try {
			const response = await fetch(
				`${this.baseUrl}/text-to-speech/${voiceId}`,
				{
					method: 'POST',
					headers: {
						Accept: 'audio/mpeg',
						'Content-Type': 'application/json',
						'xi-api-key': this.apiKey,
					},
					body: JSON.stringify(requestBody),
				}
			)

			if (!response.ok) {
				const errorText = await response.text()
				throw new Error(
					`ElevenLabs API error: ${response.status} - ${errorText}`
				)
			}

			const audioBlob = await response.blob()
			return audioBlob
		} catch (error) {
			console.error('Error generating speech:', error)
			throw error
		}
	}

	async generateSpeechStreaming(params: GenerateSpeechParams): Promise<Blob> {
		const { text, voiceId } = params

		if (!text || !voiceId) {
			throw new Error('Text and voiceId are required')
		}

		const voiceSettings = this.mapToVoiceSettings(params)

		const requestBody = {
			text,
			model_id: 'eleven_multilingual_v2',
			voice_settings: voiceSettings,
		}

		try {
			const response = await fetch(
				`${this.baseUrl}/text-to-speech/${voiceId}/stream`,
				{
					method: 'POST',
					headers: {
						Accept: 'audio/mpeg',
						'Content-Type': 'application/json',
						'xi-api-key': this.apiKey,
					},
					body: JSON.stringify(requestBody),
				}
			)

			if (!response.ok) {
				const errorText = await response.text()
				throw new Error(
					`ElevenLabs API error: ${response.status} - ${errorText}`
				)
			}

			const chunks: Uint8Array[] = []
			const reader = response.body?.getReader()

			if (!reader) {
				throw new Error('Failed to get response reader')
			}

			while (true) {
				const { done, value } = await reader.read()
				if (done) break
				if (value) chunks.push(value)
			}

			const audioBlob = new Blob(chunks, { type: 'audio/mpeg' })
			return audioBlob
		} catch (error) {
			console.error('Error generating speech with streaming:', error)
			throw error
		}
	}

	async getVoices(): Promise<any> {
		try {
			const response = await fetch(`${this.baseUrl}/voices`, {
				headers: {
					'xi-api-key': this.apiKey,
				},
			})

			if (!response.ok) {
				throw new Error('Failed to fetch voices from ElevenLabs')
			}

			const data = await response.json()
			return data.voices || []
		} catch (error) {
			console.error('Error fetching voices:', error)
			throw error
		}
	}

	/**
	 * Downloads audio blob as a file
	 *
	 * @param blob - Audio blob to download
	 * @param filename - Name of the file to save
	 */
	downloadAudio(blob: Blob, filename: string = 'generated_speech.mp3'): void {
		const url = URL.createObjectURL(blob)
		const a = document.createElement('a')
		a.href = url
		a.download = filename
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
		URL.revokeObjectURL(url)
	}

	/**
	 * Converts speech from one audio to another voice using speech-to-speech
	 *
	 * @param audioBlob - Source audio blob to convert
	 * @param params - Speech-to-speech parameters including voice settings and options
	 * @returns Promise<Blob> - Audio data in the target voice
	 */
	async speechToSpeech(
		audioBlob: Blob,
		params: SpeechToSpeechParams
	): Promise<Blob> {
		if (!audioBlob || !params.voiceId) {
			throw new Error('Audio blob and voiceId are required')
		}

		const formData = new FormData()
		formData.append('audio', audioBlob, 'audio.wav')

		const voiceSettings = {
			stability: this.normalizeSliderValue(params.stability ?? 100) || 1.0,
			similarity_boost:
				this.normalizeSliderValue(params.similarityBoost ?? 100) || 1.0,
			style: this.normalizeSliderValue(params.style ?? 0),
			use_speaker_boost: params.useSpeakerBoost !== false,
		}
		formData.append('voice_settings', JSON.stringify(voiceSettings))

		if (params.modelId) {
			formData.append('model_id', params.modelId)
		} else {
			formData.append('model_id', 'eleven_multilingual_sts_v2')
		}

		if (params.outputFormat) {
			formData.append('output_format', params.outputFormat)
		}

		if (params.removeBackgroundNoise) {
			formData.append('remove_background_noise', 'true')
		}

		try {
			const response = await fetch(
				`${this.baseUrl}/speech-to-speech/${params.voiceId}`,
				{
					method: 'POST',
					headers: {
						'xi-api-key': this.apiKey,
					},
					body: formData,
				}
			)

			if (!response.ok) {
				const errorText = await response.text()
				throw new Error(
					`ElevenLabs Speech-to-Speech error: ${response.status} - ${errorText}`
				)
			}

			const resultBlob = await response.blob()
			return resultBlob
		} catch (error) {
			console.error('Error in speech-to-speech conversion:', error)
			throw error
		}
	}

	/**
	 * Creates an audio URL from a blob for playback
	 * Remember to revoke the URL when done: URL.revokeObjectURL(url)
	 *
	 * @param blob - Audio blob
	 * @returns URL string that can be used in audio elements
	 */
	createAudioUrl(blob: Blob): string {
		return URL.createObjectURL(blob)
	}
}

export const elevenLabsService = new ElevenLabsService()
